import { type Reducer, useReducer } from 'react';

import type { Attachment, FileDto } from '../lib/types';

interface SetFileToUploadAction {
  type: 'SET_FILE_TO_UPLOAD';
  file: File;
  fileKey: string;
  abortController: AbortController;
}

interface UpdateUploadProgressAction {
  type: 'UPDATE_UPLOAD_PROGRESS';
  fileKey: string;
  percentage: number;
}

interface SetUploadCompletedAction {
  type: 'SET_UPLOAD_COMPLETED';
  fileKey: string;
  // uploadResult: { url: string };
}

interface SetUploadFailedAction {
  type: 'SET_UPLOAD_FAILED';
  fileKey: string;
}

interface RemoveAttachmentAction {
  type: 'REMOVE_ATTACHMENT';
  fileKey: string;
}

interface ResetStateAction {
  type: 'RESET_STATE';
  files: FileDto[];
}

type ReducerAction =
  | SetFileToUploadAction
  | UpdateUploadProgressAction
  | SetUploadCompletedAction
  | SetUploadFailedAction
  | RemoveAttachmentAction
  | ResetStateAction;

interface ReducerState {
  fileKeys: string[];
  attachmentsRecord: Map<string, Attachment>;
}

const stateInitializer = (initialFiles: FileDto[]): ReducerState => {
  const fileKeys = initialFiles.map((file) => file.fileKey);
  const attachmentsRecord = new Map<string, Attachment>();

  initialFiles.forEach(({ file, fileKey, fileName, mimeType, size }) =>
    attachmentsRecord.set(fileKey, {
      file,
      fileKey,
      fileName,
      mimeType,
      size,
      isDefault: true,
    }),
  );

  return { fileKeys, attachmentsRecord };
};

const reducer: Reducer<ReducerState, ReducerAction> = (state, action) => {
  switch (action.type) {
    case 'SET_FILE_TO_UPLOAD': {
      const fileKeys = state.fileKeys.concat(action.fileKey);
      const attachmentsRecord = new Map(state.attachmentsRecord);

      attachmentsRecord.set(action.fileKey, {
        file: action.file,
        fileKey: action.fileKey,
        fileName: action.file.name,
        size: action.file.size,
        mimeType: action.file.type,
        abortController: action.abortController,
        state: 'uploading',
      });

      return { fileKeys, attachmentsRecord };
    }

    case 'UPDATE_UPLOAD_PROGRESS': {
      const attachmentsRecord = new Map(state.attachmentsRecord);
      const targetAttachment = attachmentsRecord.get(action.fileKey);

      if (targetAttachment) {
        attachmentsRecord.set(action.fileKey, {
          ...targetAttachment,
          uploadProgress: action.percentage,
        });
      }

      return { ...state, attachmentsRecord };
    }

    case 'SET_UPLOAD_COMPLETED': {
      const attachmentsRecord = new Map(state.attachmentsRecord);
      const targetAttachment = attachmentsRecord.get(action.fileKey);

      if (targetAttachment) {
        attachmentsRecord.set(action.fileKey, {
          ...targetAttachment,
          state: 'completed',
          uploadProgress: null,
          abortController: null,
          // url: action.uploadResult.url,
        });
      }

      return { ...state, attachmentsRecord };
    }

    case 'SET_UPLOAD_FAILED': {
      const attachmentsRecord = new Map(state.attachmentsRecord);
      const targetAttachment = attachmentsRecord.get(action.fileKey);

      if (targetAttachment) {
        attachmentsRecord.set(action.fileKey, {
          ...targetAttachment,
          state: 'failed',
          uploadProgress: null,
          abortController: null,
        });
      }

      return { ...state, attachmentsRecord };
    }

    case 'REMOVE_ATTACHMENT': {
      const targetAttachment = state.attachmentsRecord.get(action.fileKey);

      if (!targetAttachment) {
        return state;
      }

      const fileKeys = state.fileKeys.filter((fileKey) => fileKey !== action.fileKey);
      const attachmentsRecord = new Map(state.attachmentsRecord);
      attachmentsRecord.delete(action.fileKey);

      return { fileKeys, attachmentsRecord };
    }

    case 'RESET_STATE': {
      return stateInitializer(action.files);
    }

    default:
      return state;
  }
};

export const useAttachmentsReducer = (initialFiles: FileDto[]) => {
  return useReducer(reducer, initialFiles, stateInitializer);
};
